/**
 * LPP 50% deposit payment schedule experiment (payments_lpp_50_percent_deposit_schedule).
 *
 * Trigger: Reaching checkout with an LPP (bedbank) order > $1,999 (AU), refundable and non-Expedia.
 * - Control: no payment schedule UI (schedules suppressed).
 * - Variant (on): show 50% deposit schedule via override template, even when the rate has no native payment schedule template.
 */
import { createSelector } from 'reselect'
import { isSpoofed } from 'selectors/isSpoofed'
import { findPostPurchaseCheckout, isBedbankItem } from 'lib/checkout/checkoutUtils'
import { getBedbankRateKey } from 'lib/hotels/getBedbankRateKey'
import { LPP_DEPOSIT_MINIMUM_VALUE } from 'constants/payment'
import getPayableTotal from '../getPayableTotal'
import isPaymentScheduleEnabled from './isPaymentScheduleEnabled'
import { isLppHotelOffer } from 'lib/offer/offerTypes'
import { getExperimentEnabledDecision, getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { Experiments } from 'constants/experiments'

const LPP_50_EXPERIMENT_ID = Experiments.paymentsLpp50PercentDepositSchedule.id
const EXPEDIA_SUPPLIER_KEYWORD = 'expedia'

function isLppItemEligibleForExperiment(
  item: App.Checkout.BedbankHotelItem,
  offers: Record<string, App.AnyOffer>,
  bedbankOffers: Record<string, App.BedbankOffer>,
  bedbankRatesByOffer: App.State['offer']['bedbankOfferRates'],
): boolean {
  const checkoutOffer = offers[item.offerId]
  const offer = (isLppHotelOffer(checkoutOffer) ? checkoutOffer : undefined) ?? bedbankOffers[item.offerId]
  const isLppOffer = isLppHotelOffer(offer)

  const rateKey = getBedbankRateKey([item.occupancy], item.checkIn, item.checkOut)
  const rates = bedbankRatesByOffer[item.offerId]?.[rateKey]
  const matchedRate = rates?.find(rate => rate.id === item.roomRateId)

  const isRefundable = !!matchedRate && (matchedRate.refundable || item.refundable === true)

  const supplier = `${matchedRate?.supplier ?? matchedRate?.supplierCode ?? ''}`.toLowerCase()
  const isNonExpediaSupplier = !supplier.includes(EXPEDIA_SUPPLIER_KEYWORD)

  const result = isLppOffer && !!matchedRate && isRefundable && isNonExpediaSupplier

  return result
}

/**
 * Carts that match the experiment trigger:
 * - AU region
 * - not spoofed
 * - not post-purchase
 * - payment schedules enabled
 * - order payable total > 1,999
 * - bedbank-only cart items
 * - all cart items are refundable non-Expedia LPP bedbank rates (no template required — 50% schedule is applied as override)
 */
export const isLpp50DepositExperimentTriggerCart = createSelector(
  (state: App.State) => isSpoofed(state),
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => isPaymentScheduleEnabled(state),
  (state: App.State) => getPayableTotal(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => state.offer.bedbankOfferRates,
  (
    spoofed,
    regionCode,
    postPurchase,
    paymentScheduleEnabled,
    payableTotal,
    cartItems,
    offers,
    bedbankOffers,
    bedbankRatesByOffer,
  ): boolean => {
    const hasGatingBlockers = spoofed || regionCode !== 'AU' || postPurchase || !paymentScheduleEnabled
    const isAboveThreshold = payableTotal > LPP_DEPOSIT_MINIMUM_VALUE
    const bedbankItems = cartItems.filter(isBedbankItem)
    const hasBedbankItems = bedbankItems.length > 0
    const allBedbankItemsEligible = hasBedbankItems && bedbankItems.every(item => isLppItemEligibleForExperiment(
      item,
      offers,
      bedbankOffers,
      bedbankRatesByOffer,
    ))
    const result = !hasGatingBlockers && isAboveThreshold && hasBedbankItems && allBedbankItemsEligible
    return result
  },
)

/**
 * Bucketed control: hide all payment schedules for trigger carts.
 * Uses variationKey from the experiment object directly — getOptimizelyExperimentVariation
 * returns undefined for control because enabled is false for that arm.
 */
export const isLpp50DepositExperimentControlSuppressesSchedules = createSelector(
  isLpp50DepositExperimentTriggerCart,
  (state: App.State) => state.optimizely.optimizelyExperiments[LPP_50_EXPERIMENT_ID]?.variationKey,
  (trigger, variationKey): boolean => {
    const result = trigger && variationKey?.toLowerCase() === 'control'

    return result
  },
)

/** Variant on: allow LPP 50% schedule behavior for trigger carts. */
export const isLpp50DepositExperimentVariantForces50Schedule = createSelector(
  isLpp50DepositExperimentTriggerCart,
  (state: App.State) => getOptimizelyExperimentVariation(state, LPP_50_EXPERIMENT_ID),
  (trigger, variation): boolean => trigger && getExperimentEnabledDecision(variation),
)
